import React, { useMemo } from "react";
import { AbsoluteFill, useCurrentFrame } from "remotion";

interface ColorOverlay {
  r: number;
  g: number;
  b: number;
  opacity: number;
}

interface VisualEffectStyle {
  entrance: string;
  grainIntensity?: number;
  flickerSpeed?: number;
  flickerDepth?: number;
  hueRotate?: number;
  saturate?: number;
  contrast?: number;
  brightness?: number;
  scanlineOpacity?: number;
  vignetteStrength?: number;
  colorOverlay?: ColorOverlay | null;
  rgbSplit?: number;
}

interface VisualEffectProps {
  style: VisualEffectStyle;
  children?: React.ReactNode;
}

/**
 * VisualEffect 视觉特效组件
 *
 * 在画面上叠加颗粒噪声、频闪、色偏、扫描线、暗角等恐怖风格效果。
 * 可作为独立层放入 layers[]，也可以包裹 children 内容。
 *
 * 所有效果都用 CSS + SVG 实现，不依赖额外库。通过 AbsoluteFill 叠层定位，
 * 各效果层互相独立，不会阻断下方内容的事件/渲染。
 */
const VisualEffect: React.FC<VisualEffectProps> = ({ style: s, children }) => {
  const frame = useCurrentFrame();

  // ── 颗粒噪声 —— 用 SVG feTurbulence 生成 ──
  const grainLayer = useMemo(() => {
    if (!s.grainIntensity || s.grainIntensity <= 0) return null;

    // 每 12 帧刷新噪声 seed，降低闪烁频率
    const seed = Math.floor(frame / 12);
    const opacity = s.grainIntensity;

    return (
      <AbsoluteFill
        key={`grain-${seed}`}
        style={{
          mixBlendMode: "overlay" as React.CSSProperties["mixBlendMode"],
          opacity,
          pointerEvents: "none",
        }}
      >
        <svg width="100%" height="100%" style={{ display: "block" }}>
          <defs>
            <filter id={`noise-${seed}`}>
              <feTurbulence
                type="fractalNoise"
                baseFrequency="0.9"
                numOctaves={4}
                stitchTiles="stitch"
                seed={seed}
              />
              <feColorMatrix type="saturate" values="0" />
            </filter>
          </defs>
          <rect
            width="100%"
            height="100%"
            filter={`url(#noise-${seed})`}
            opacity={opacity}
          />
        </svg>
      </AbsoluteFill>
    );
  }, [s.grainIntensity, frame]);

  // ── 频闪 —— 根据帧号切换透明度 ──
  const flickerLayer = useMemo(() => {
    const speed = s.flickerSpeed ?? 0;
    const depth = s.flickerDepth ?? 0;
    if (speed <= 0 || depth <= 0) return null;

    // 每 30fps 一秒，用正弦波控制明暗切换
    const flickerCycle = 30 / speed;
    const phase = (frame % flickerCycle) / flickerCycle;
    // 快速切换：低于 50% 时变暗
    const isDark = phase < 0.45;
    const opacity = isDark ? depth : 0;
    if (opacity <= 0) return null;

    return (
      <AbsoluteFill
        style={{
          backgroundColor: "#000000",
          opacity,
          pointerEvents: "none",
        }}
      />
    );
  }, [s.flickerSpeed, s.flickerDepth, frame]);

  
  // ── 扫描线 ──
  const scanlineLayer = useMemo(() => {
    const opacity = s.scanlineOpacity ?? 0;
    if (opacity <= 0) return null;

    return (
      <AbsoluteFill
        style={{
          background: `repeating-linear-gradient(
            to bottom,
            transparent,
            transparent 2px,
            rgba(0,0,0,${opacity}) 2px,
            rgba(0,0,0,${opacity}) 4px
          )`,
          pointerEvents: "none",
        }}
      />
    );
  }, [s.scanlineOpacity]);

  // ── 暗角（径向渐变） ──
  const vignetteLayer = useMemo(() => {
    const strength = s.vignetteStrength ?? 0;
    if (strength <= 0) return null;

    return (
      <AbsoluteFill
        style={{
          background: `radial-gradient(
            ellipse at center,
            transparent ${Math.max(0, 50 - strength * 20)}%,
            rgba(0,0,0,${Math.min(0.9, strength)}) 100%
          )`,
          pointerEvents: "none",
        }}
      />
    );
  }, [s.vignetteStrength]);

  // ── 颜色叠加 ──
  const colorOverlayLayer = useMemo(() => {
    if (!s.colorOverlay) return null;
    const { r, g, b, opacity } = s.colorOverlay;
    if (opacity <= 0) return null;

    return (
      <AbsoluteFill
        style={{
          backgroundColor: `rgba(${r * 255},${g * 255},${b * 255},${opacity})`,
          mixBlendMode: "overlay" as React.CSSProperties["mixBlendMode"],
          pointerEvents: "none",
        }}
      />
    );
  }, [s.colorOverlay]);

  // ── RGB 分裂（红蓝偏移） ──
  const rgbSplitLayer = useMemo(() => {
    const split = s.rgbSplit ?? 0;
    if (split <= 0) return null;

    // 带轻微波动的偏移量
    const drift = Math.sin(frame * 0.1) * 1;
    const offset = split + drift;

    return (
      <AbsoluteFill style={{ pointerEvents: "none" }}>
        {/* 红色通道偏移 */}
        <AbsoluteFill
          style={{
            mixBlendMode: "screen" as any,
            opacity: 0.5,
            transform: `translateX(${offset}px)`,
            filter: "sepia(1) saturate(100) hue-rotate(0deg)",
          }}
        />
        {/* 蓝色通道偏移 */}
        <AbsoluteFill
          style={{
            mixBlendMode: "screen" as any,
            opacity: 0.5,
            transform: `translateX(${-offset}px)`,
            filter: "sepia(1) saturate(100) hue-rotate(180deg)",
          }}
        />
      </AbsoluteFill>
    );
  }, [s.rgbSplit, frame]);

  return (
    <AbsoluteFill>
      {children}

      {/* 效果叠加层（从下到上） */}
      {grainLayer}
      {scanlineLayer}
      {colorOverlayLayer}
      {rgbSplitLayer}
      {vignetteLayer}
      {flickerLayer}
    </AbsoluteFill>
  );
};

export default VisualEffect;
;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
